import { useEffect } from 'react';
import { useLocation } from 'react-router';

import { deriveDocumentTitle } from 'src/common/utils/documentTitle';
import { useTranslation } from 'src/i18n/shim/useTranslation';

/**
 * Keeps the document title in step with the current route and returns the page
 * label for a polite live region, so screen-reader users are told the page
 * changed after a client-side (SPA) navigation — which, unlike a full page load,
 * the browser does not announce on its own. Together these cover WCAG 2.4.2
 * (Page Titled) and the SPA route-change announcement gap.
 *
 * The caller renders the returned label into an `aria-live="polite"` region.
 * That region must be mounted before its text changes for the change to be
 * announced, so this belongs in the persistent AppShell, not a per-page mount.
 * The empty label on the home/root shell intentionally announces nothing.
 */
export const useRouteAnnouncement = (): string => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { title, label } = deriveDocumentTitle(t, pathname);

  useEffect(() => {
    document.title = title;
  }, [title]);

  return label;
};
